import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { CargoCountService } from '@/services/cargoCountService';
import type { CargoCountState, AreaSummary, CargoCountFilters } from '@/types/cargoCount.types';

// Default filter state
const defaultFilters: CargoCountFilters = {
  selectedAreas: [], // Empty = all areas
  periodRange: 'all',
};

/**
 * Cargo Count Store
 * Manages cargo count data, filters, and computed values
 */
export const useCargoCountStore = create<CargoCountState>()(
  devtools(
    (set, get) => ({
      // Initial State
      data: [],
      areaSummaries: [],
      filters: defaultFilters,
      loading: false,
      error: null,

      /**
       * Fetch cargo count data from API
       */
      fetchCargoCount: async () => {
        set({ loading: true, error: null });
        try {
          const data = await CargoCountService.getCargoCount();
          set({
            data,
            loading: false,
          });
          get().getAreaSummaries();
        } catch (error) {
          set({
            error: error instanceof Error ? error.message : 'Failed to fetch cargo count data',
            loading: false,
          });
        }
      },

      /**
       * Update filters
       */
      setFilters: (newFilters: Partial<CargoCountFilters>) => {
        set((state) => ({
          filters: {
            ...state.filters,
            ...newFilters,
          },
        }));
      },

      /**
       * Get filtered data based on current filters
       */
      getFilteredData: () => {
        const { data, filters } = get();
        let filtered = [...data];

        // Filter by selected areas
        if (filters.selectedAreas.length > 0) {
          filtered = filtered.filter((item) => filters.selectedAreas.includes(item.area));
        }

        // Sort by period (most recent first)
        filtered.sort((a, b) => {
          // For now, simple string comparison
          // TODO: Implement proper date parsing if needed
          return b.period.localeCompare(a.period);
        });

        return filtered;
      },

      /**
       * Calculate area summaries (total fixed, expected, average per area)
       */
      getAreaSummaries: () => {
        const { data } = get();

        // Import AREA_COLORS at runtime to avoid circular dependency
        const areaColors: Record<string, string> = {
          AG: '#3b82f6',
          SAM: '#10b981',
          USG: '#f59e0b',
          WAF: '#8b5cf6',
        };

        // Group by area and calculate totals
        const summaryMap = data.reduce(
          (acc, item) => {
            if (!acc[item.area]) {
              acc[item.area] = {
                area: item.area,
                totalFixed: 0,
                totalExpected: 0,
                totalAverage: 0,
                color: areaColors[item.area] || '#6b7280',
              };
            }

            acc[item.area].totalFixed += item.fixed;
            acc[item.area].totalExpected += item.expected;
            acc[item.area].totalAverage += item.average;

            return acc;
          },
          {} as Record<string, AreaSummary>
        );

        const summaries = Object.values(summaryMap).sort((a, b) => a.area.localeCompare(b.area));

        set({ areaSummaries: summaries });
        return summaries;
      },

      /**
       * Refresh data (re-fetch from API)
       */
      refreshData: async () => {
        await get().fetchCargoCount();
      },
    }),
    { name: 'CargoCountStore' }
  )
);
